/**
 * Add-on catalog screen (TMMPDM-0019, TMMAPI-0039): the catalogue — create, edit, and the
 * DRAFT → ACTIVE → RETIRED lifecycle — and every tenant's add-ons with a status filter.
 * Offering an item to a tenant lives on the tenant's Add-ons tab.
 * TMMPDM-0100: Catalogue and Tenant add-ons are tabs (`?tab=`).
 */
import { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { FilterTabs } from '@/components/ui/FilterTabs';
import {
  EditDetailsDialog,
  ErrorState,
  PageHeader,
  fieldEditable,
  useNotify,
  type EditDetailField,
} from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import type { AddonAllowedBilling, CatalogPatchRequest, CatalogView } from '@/api/tmm/types';
import { parseMajor } from '@/routes/rateCards/rateCardInputs';
import { majorFromMinor } from '@/format';
import { invalidMoney } from './addons/addonFormat';
import { CatalogTable } from './addons/CatalogTable';
import { CatalogItemDialog } from './addons/CatalogItemDialog';
import { FleetAddonsTable } from './addons/FleetAddonsTable';
import { useCatalog } from './addons/useCatalog';

const ADDON_TABS = ['catalog', 'tenants'] as const;
type AddonTab = (typeof ADDON_TABS)[number];
const ADDON_VIEW: UrlSchema<{ tab: AddonTab }> = {
  tab: { default: 'catalog', parse: oneOf(ADDON_TABS) },
};

export function AddOns() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { items, loading, error, refetch, refreshDetails, acceptSaved } = useCatalog();
  const [dialog, setDialog] = useState<{ item: CatalogView | null } | null>(null);
  const [detailsKind, setDetailsKind] = useState<string | null>(null);
  const [detailsToken, setDetailsToken] = useState(0);
  const [view, setView] = useUrlState(ADDON_VIEW);
  const notify = useNotify();
  // Action failures stay a banner; quiet success is a toast (TMMPDM-0034).
  const [fleetKey, setFleetKey] = useState(0);

  const detailsItem = items.find((item) => item.kind === detailsKind) ?? null;

  const catalogFields = (item: CatalogView): EditDetailField[] => {
    const fields: EditDetailField[] = [];
    if (fieldEditable(item.editableFields, 'name')) {
      fields.push({
        key: 'name',
        label: t('addons.form.name', 'Name'),
        value: item.name,
        required: true,
        maxLength: 120,
      });
    }
    if (fieldEditable(item.editableFields, 'description')) {
      fields.push({
        key: 'description',
        label: t('addons.form.description', 'Description'),
        value: item.description,
        kind: 'multiline',
        required: true,
        maxLength: 2000,
      });
    }
    if (fieldEditable(item.editableFields, 'defaultPriceMinor')) {
      fields.push({
        key: 'defaultPriceMinor',
        label: t('addons.form.defaultPrice', 'Default price'),
        value:
          item.defaultPriceMinor === undefined
            ? ''
            : majorFromMinor(item.defaultPriceMinor, item.currency),
        kind: 'money',
        helperText: t('components.editDetails.futureOffers', 'Applies to future offers only.'),
      });
    }
    if (fieldEditable(item.editableFields, 'allowedBilling')) {
      fields.push({
        key: 'allowedBilling',
        label: t('addons.form.allowedBilling', 'Allowed billing'),
        value: item.allowedBilling,
        kind: 'select',
        options: (['CARD', 'NET_FROM_PAYOUTS', 'BOTH'] as const).map((value) => ({
          value,
          label: t(`addons.allowedBilling.${value}`, value),
        })),
      });
    }
    return fields;
  };

  const saved = (text: string) => {
    notify.success(text);
    refetch();
    setFleetKey((n) => n + 1);
  };

  return (
    <Box>
      <PageHeader
        title={t('page.addOns.title', 'Add-on catalog')}
        subtitle={t('page.addOns.subtitle', 'What the platform sells to tenants, and who has it.')}
        {...(view.tab === 'catalog'
          ? {
              primaryAction: {
                label: t('addons.actions.new', 'New add-on…'),
                onClick: () => setDialog({ item: null }),
              },
            }
          : {})}
      />

      <FilterTabs
        ariaLabel={t('addons.tabs.aria', 'Add-on sections')}
        active={view.tab}
        onChange={(next) => {
          const tab = oneOf(ADDON_TABS)(next);
          if (tab) setView({ tab });
        }}
        tabs={[
          { key: 'catalog', label: t('addons.catalog.title', 'Catalogue') },
          { key: 'tenants', label: t('addons.fleet.title', 'Tenant add-ons') },
        ]}
      />

      {view.tab === 'catalog' ? (
        error ? (
          <Box sx={{ mt: 2 }}>
            <ErrorState
              error={error}
              message={t('addons.catalog.loadError', 'Could not load the catalogue.')}
              onRetry={refetch}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <CatalogTable
              items={items}
              loading={loading}
              locale={locale}
              onEdit={(item) => setDialog({ item })}
              onEditDetails={(item) => setDetailsKind(item.kind)}
              onChanged={(item, kind) =>
                saved(
                  kind === 'activated'
                    ? t('addons.activated', '{{name}} is active.', { name: item.name })
                    : t('addons.retired', '{{name}} is retired.', { name: item.name }),
                )
              }
            />
          </Box>
        )
      ) : null}

      {view.tab === 'tenants' ? (
        <Box sx={{ mt: 2 }}>
          <FleetAddonsTable locale={locale} refreshKey={fleetKey} />
        </Box>
      ) : null}

      <EditDetailsDialog
        open={detailsItem !== null}
        entityLabel={t('components.editDetails.entity.addon', 'add-on')}
        fields={detailsItem ? catalogFields(detailsItem) : []}
        version={detailsItem ? detailsItem.version : null}
        validate={(values) => {
          const next: Record<string, string> = {};
          const price = values.defaultPriceMinor;
          if (price === undefined || !detailsItem) return next;
          if (price.trim() === '' && detailsItem.defaultPriceMinor !== undefined) {
            next.defaultPriceMinor = t(
              'components.cannotClear',
              "This can't be cleared yet. Enter a value, or leave it as it was.",
            );
          } else if (price.trim() !== '' && invalidMoney(price)) {
            next.defaultPriceMinor = t('addons.form.nonNegative', 'Must be zero or greater.');
          }
          return next;
        }}
        onClose={() => setDetailsKind(null)}
        onRefreshAfterSave={refreshDetails}
        onReload={async () => {
          await refreshDetails();
          setDetailsToken((n) => n + 1);
        }}
        reloadToken={detailsToken}
        onSubmit={async (changed, version) => {
          if (!detailsItem) return;
          const body: CatalogPatchRequest = {};
          if (changed.name !== undefined) body.name = changed.name.trim();
          if (changed.description !== undefined) body.description = changed.description.trim();
          if (changed.defaultPriceMinor !== undefined && changed.defaultPriceMinor.trim() !== '') {
            body.defaultPriceMinor = parseMajor(
              changed.defaultPriceMinor.trim(),
              detailsItem.currency,
            );
          }
          if (changed.allowedBilling !== undefined) {
            body.allowedBilling = changed.allowedBilling as AddonAllowedBilling;
          }
          if (version !== null) body.version = version;
          acceptSaved(await adminApi.patchAddonCatalog(detailsItem.kind, body));
        }}
      />
      <CatalogItemDialog
        open={dialog !== null}
        item={dialog?.item ?? null}
        onClose={() => setDialog(null)}
        onSaved={(item) => {
          const created = dialog?.item === null;
          setDialog(null);
          saved(
            created
              ? t('addons.created', '{{name}} was added as a draft.', { name: item.name })
              : t('addons.updated', '{{name}} was saved.', { name: item.name }),
          );
        }}
      />
    </Box>
  );
}
